import { BoardIntegration } from "frontend/hooks/use-board-integrations.types";
import { PlanData, User } from "shared/datamodel/schemas";
import { Board } from "shared/datamodel/schemas/board";
import { IntegrationType } from "shared/integrations/integration";
import { parse } from "superjson";
import { isCIM } from "utils/url-utils";
import { isClient } from "utils/window-utils";

const hasBeenCached = new Set<string>();

export function getInitialRepsToken() {
  return getProps<string>({ key: "repsTokenFromServer", getOnce: !isCIM() });
}

export function getInitialAccountIntegration() {
  return getProps<{ id: string; type: IntegrationType; integration_account_id: string }[]>({
    key: "accountIntegration",
    getOnce: true,
  });
}

export function getInitialMaxCollaborators() {
  return getProps<number>({ key: "maxCollaborators", getOnce: true });
}

export function getInitialBoardIntegrations() {
  return getProps<BoardIntegration[]>({ key: "boardIntegrations", getOnce: true });
}

export function getInitialUser() {
  return getProps<User>({ key: "user", getOnce: true });
}

export function getInitialAuthStatus() {
  return getProps<{ authenticated: boolean | null; blocked: boolean | null; token?: string | null }>({
    key: "authStatus",
    getOnce: true,
  });
}

export function getInitialBoard() {
  return getProps<{ board: Board; templateElements: Record<string, any> | null }>({ key: "board", getOnce: true });
}

export function getInitialCsrfToken() {
  return getProps<string>({ key: "csrfToken", getOnce: false });
}

export function getInitialPlansData() {
  return getProps<PlanData[]>({ key: "plansData", getOnce: false });
}

// Utility function to get all cached server props at once
export function getCachedServerProps(): Record<string, any> | null {
  if (!isClient()) {
    return null;
  }
  try {
    let props = JSON.parse(
      document?.getElementById
        ? document?.getElementById("__NEXT_DATA__")?.textContent ?? "{}"
        : document?.textContent ?? "{}"
    )?.props?.pageProps;

    if (props) {
      const canvasContent = sessionStorage.getItem("canvasContent");
      if (canvasContent) {
        props = parse(canvasContent);
      }
    }

    if (!props) {
      return null;
    }
    return props;
  } catch {
    return null;
  }
}

const getProps = <T>({ key, getOnce = false }: { key: string; getOnce?: boolean }): T | null => {
  if (getOnce) {
    if (hasBeenCached.has(key)) {
      return null;
    }
    hasBeenCached.add(key);
  }
  const props = getCachedServerProps();
  if (!props || Object.keys(props).length === 0 || props?.["errors"]) {
    return null;
  }
  if (key in props && props?.[key] !== undefined) {
    return parse(props?.[key]) as T;
  }
  return null;
};
